package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.presentation.home.model.icon
import com.patronat.top.client.presentation.home.model.labelRes
import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueGroup
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_all_categories_title
import top.clientapp.generated.resources.client_category_search_placeholder

@Composable
fun AllCategoriesContent(
    searchQuery: String,
    expandedGroups: Set<VenueGroup>,
    onBackClick: () -> Unit,
    onSearchQueryChange: (String) -> Unit,
    onGroupClick: (VenueGroup) -> Unit,
    onCategoryClick: (VenueCategory) -> Unit,
    modifier: Modifier = Modifier
) {
    val categoryGroups = visibleCategoryGroups(searchQuery)
    val showSearchResults = searchQuery.isNotBlank()

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 24.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clickable(onClick = onBackClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.ArrowLeft,
                    contentDescription = null,
                    tint = MineShaft950,
                    modifier = Modifier.size(20.dp)
                )
            }

            Text(
                text = stringResource(Res.string.client_all_categories_title),
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 14.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Medium
                )
            )
        }

        AppSearchField(
            value = searchQuery,
            onValueChange = onSearchQueryChange,
            placeholder = stringResource(Res.string.client_category_search_placeholder),
            modifier = Modifier.fillMaxWidth()
        )

        Spacer(modifier = Modifier.height(16.dp))

        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            items(
                items = categoryGroups,
                key = { it.group.name }
            ) { groupItem ->
                val expanded = showSearchResults || groupItem.group in expandedGroups
                CategoryGroupCard(
                    group = groupItem.group,
                    categories = groupItem.categories,
                    expanded = expanded,
                    onGroupClick = { onGroupClick(groupItem.group) },
                    onCategoryClick = onCategoryClick
                )
            }
        }
    }
}

@Composable
private fun CategoryGroupCard(
    group: VenueGroup,
    categories: List<VenueCategory>,
    expanded: Boolean,
    onGroupClick: () -> Unit,
    onCategoryClick: (VenueCategory) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(White)
            .border(1.dp, MineShaft100, RoundedCornerShape(8.dp))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .clickable(onClick = onGroupClick)
                .padding(horizontal = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(CategoryIconBackground),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = group.icon(),
                    contentDescription = null,
                    tint = Color.Unspecified,
                    modifier = Modifier.size(20.dp)
                )
            }

            Spacer(modifier = Modifier.size(10.dp))

            Text(
                text = stringResource(group.labelRes()),
                style = BodyMedium.copy(
                    color = MineShaft900,
                    fontSize = 14.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Normal
                ),
                modifier = Modifier.weight(1f)
            )

            Icon(
                painter = AppIcons.ArrowDown,
                contentDescription = null,
                tint = MineShaft950,
                modifier = Modifier
                    .size(16.dp)
                    .rotate(if (expanded) 180f else 0f)
            )
        }

        if (expanded) {
            HorizontalDivider(
                thickness = 1.dp,
                color = MineShaft100
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 8.dp, bottom = 10.dp)
            ) {
                categories.forEach { category ->
                    Text(
                        text = stringResource(category.labelRes()),
                        style = BodyMedium.copy(
                            color = MineShaft600,
                            fontSize = 13.sp,
                            lineHeight = 20.sp,
                            fontWeight = FontWeight.Normal
                        ),
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onCategoryClick(category) }
                            .padding(horizontal = 18.dp, vertical = 6.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun visibleCategoryGroups(searchQuery: String): List<CategoryGroupItem> {
    val query = searchQuery.trim().lowercase()

    return VenueGroup.entries.mapNotNull { group ->
        val groupLabel = stringResource(group.labelRes())
        val categories = VenueCategory.entries
            .filter { it.group == group }
            .filter { category ->
                if (query.isBlank()) {
                    true
                } else {
                    val categoryLabel = stringResource(category.labelRes())
                    groupLabel.lowercase().contains(query) ||
                        categoryLabel.lowercase().contains(query)
                }
            }

        if (categories.isEmpty()) {
            null
        } else {
            CategoryGroupItem(
                group = group,
                categories = categories
            )
        }
    }
}

private data class CategoryGroupItem(
    val group: VenueGroup,
    val categories: List<VenueCategory>
)

private val CategoryIconBackground = Color(0xFFF6F6F6)
